'use client';

import React from 'react';
import { MagicLinkContinue, type MagicLinkContinueProps } from '../MagicLinkContinue/MagicLinkContinue';
import { LoginContainer } from './common/LoginContainer';
import { LoginToVerifiMeTitle } from './common/LoginTitle';
import { SignInState } from './constants';
import MainLogin, { TMainLoginProps } from './MainLogin';
import PasswordlessLoginContextProvider from './PasswordlessLoginContextProvider';
import { SigningYouIn } from './SigningYouIn';
import SignInLinkRequested, { type SignInLinkRequestedResendProps } from './SignInLinkRequested';
import ValidatingSignInStatus from './ValidatingSignInStatus';

export default function PasswordlessLogin({
  signInState,
  children,
  dataTestId,
  magicLinkPending,
  signInLinkRequested,
  ...props
}: {
  children?: React.ReactNode;
  signInState: SignInState;
  dataTestId?: string;
  /**
   * Copy and slots for the MAGIC_LINK_PENDING screen (MagicLinkContinue).
   * The container is responsible for supplying this whenever it sets
   * signInState to MAGIC_LINK_PENDING; omitted (or omitted while in that
   * state), the screen renders nothing rather than guessing at copy this
   * component has no business authoring (see MagicLinkContinue's own doc
   * comment and ADR-0050).
   */
  magicLinkPending?: MagicLinkContinueProps;
  /**
   * Copy, slots and the handler for the resend affordance on the SIGNIN_LINK_REQUESTED screen
   * (SignInLinkRequested). Forwarded verbatim as that component's `resend` prop; omitted, the
   * screen renders exactly as it always has - a static status message - so an existing caller
   * sees no change. See SignInLinkRequested's own doc comments for why this needs a fresh code
   * rather than resending silently.
   */
  signInLinkRequested?: SignInLinkRequestedResendProps;
} & TMainLoginProps) {
  // MAGIC_LINK_PENDING and SIGNING_YOU_IN render outside LoginContainer: LoginContainer pins a fixed
  // width built for the other four states' form-shaped content, and both of these are design-board
  // screens that size themselves (their own `width: '100%'`/`maxWidth` sx, matching the board's own
  // stylesheet rule) and draw no card chrome. Wrapping either in LoginContainer clipped its content
  // column to LoginContainer's fixed width and added a card the design does not draw.
  const rendersWithoutLoginContainer =
    SignInState.MAGIC_LINK_PENDING === signInState || SignInState.SIGNING_YOU_IN === signInState;

  return (
    <PasswordlessLoginContextProvider lastSignedInEmail={props.lastSignedInEmail}>
      {rendersWithoutLoginContainer ? (
        <>
          {/* No LoginToVerifiMeTitle on either branch, unlike every other state: each screen's own
              heading is the page heading (MagicLinkContinue's caller-supplied `title`, and board
              screen 1b's own "Signing you in ..."), and stacking the generic "Login to VerifiMe"
              above it would double up headings the board never draws.

              No fallback element when magicLinkPending is omitted: dataTestId only ever lands on
              MagicLinkContinue's own root, the one thing there with anything to hook onto, so that
              state renders nothing at all rather than an empty stand-in kept alive only to give a
              test something to find. */}
          {SignInState.MAGIC_LINK_PENDING === signInState &&
            magicLinkPending && <MagicLinkContinue {...magicLinkPending} data-testid={dataTestId} />}
          {SignInState.SIGNING_YOU_IN === signInState && <SigningYouIn data-testid={dataTestId} />}
        </>
      ) : (
        <LoginContainer data-testid={dataTestId}>
          {SignInState.NOT_SIGNED_IN === signInState && (
            <>
              <LoginToVerifiMeTitle />
              <MainLogin {...props} />
            </>
          )}
          {SignInState.SIGNIN_LINK_REQUESTED === signInState && (
            <>
              {/* Omitted once resend is supplied: LinkSent (rendered inside SignInLinkRequested
                  below) draws its own "Email sent!" heading, and stacking the generic title above
                  it would double up headings the design board never draws - the same reasoning
                  the MAGIC_LINK_PENDING branch above already applies for MagicLinkContinue's own
                  title. Still shown for the no-resend fallback (an existing caller with the
                  isInertMagicLinkEnabled toggle off), which renders exactly as it always has. */}
              {!signInLinkRequested && <LoginToVerifiMeTitle />}
              <SignInLinkRequested resend={signInLinkRequested} />
            </>
          )}
          {SignInState.VALIDATE_SIGN_IN_STATUS === signInState && (
            <>
              <LoginToVerifiMeTitle />
              <ValidatingSignInStatus />
            </>
          )}
          {SignInState.SIGNED_IN === signInState && children && (
            <>
              <LoginToVerifiMeTitle />
              {children}
            </>
          )}
        </LoginContainer>
      )}
    </PasswordlessLoginContextProvider>
  );
}
